import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BadgeCheck,
  Ban,
  CheckCircle2,
  Clock3,
  MoreHorizontal,
  LogIn,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import { api, type AppUser, type ImpersonationResult } from "@/lib/api";
import { EmptyState, ErrorBox, Spinner } from "@/components/ui";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const fmt = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
    : "Never";

function UserIdentity({ user }: { user: AppUser }) {
  const label = user.email ?? user.username ?? "Anonymous user";
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {label.slice(0, 1).toUpperCase()}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-sm font-medium text-text-primary">{label}</p>
          {user.email_verified ? <BadgeCheck className="h-4 w-4 shrink-0 text-success" aria-label="Email verified" /> : null}
        </div>
        <p className="truncate font-mono text-[11px] text-text-muted" title={user.id}>{user.id}</p>
      </div>
    </div>
  );
}

export function UsersPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [busyID, setBusyID] = useState<string | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<AppUser | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.listAppUsers(id, {
        status: status || undefined,
        q: query.trim() || undefined,
        sort,
      });
      setUsers(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id, query, sort, status]);

  useEffect(() => {
    if (id) void load();
  }, [id, load]);

  const summary = useMemo(() => ({
    total: users.length,
    verified: users.filter((u) => u.email_verified).length,
    pending: users.filter((u) => u.email && !u.email_verified).length,
    disabled: users.filter((u) => u.status === "disabled").length,
  }), [users]);

  async function run(user: AppUser, action: () => Promise<unknown>, reload = true) {
    setBusyID(user.id);
    setError(null);
    try {
      await action();
      if (reload) await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusyID(null);
    }
  }

  async function verifyEmail() {
    if (!verifyTarget) return;
    const user = verifyTarget;
    await run(user, () => api.verifyAppUserEmail(id, user.id));
    setVerifyTarget(null);
  }

  function actions(user: AppUser) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" aria-label={`Actions for ${user.email ?? user.username ?? user.id}`}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {user.email && !user.email_verified && user.status === "active" ? (
            <DropdownMenuItem onClick={() => setVerifyTarget(user)}>
              <ShieldCheck className="mr-2 h-4 w-4" /> Verify email
            </DropdownMenuItem>
          ) : null}
          {user.status !== "anonymous" ? (
            <DropdownMenuItem onClick={() => void run(user, () => api.setAppUserStatus(id, user.id, user.status === "active" ? "disabled" : "active"))}>
              <Ban className="mr-2 h-4 w-4" /> {user.status === "active" ? "Disable user" : "Enable user"}
            </DropdownMenuItem>
          ) : null}
          {(user.status === "active" || user.status === "anonymous") ? (
            <DropdownMenuItem onClick={() => {
              if (confirm(`Impersonate ${user.email ?? user.username ?? user.id}? This audited token is short-lived.`)) {
                void run(user, async () => {
                  const { data } = await api.impersonateAppUser(id, user.id);
                  setImpersonation(data);
                }, false);
              }
            }}>
              <LogIn className="mr-2 h-4 w-4" /> Impersonate
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onClick={() => void run(user, () => api.revokeAppUserSessions(id, user.id), false)}>
            <RefreshCw className="mr-2 h-4 w-4" /> Revoke sessions
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-danger" onClick={() => {
            if (confirm(`Delete ${user.email ?? user.username ?? user.id}? This cannot be undone.`)) {
              void run(user, () => api.deleteAppUser(id, user.id));
            }
          }}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete user
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-primary">Authentication</p>
          <h1 className="text-2xl font-semibold tracking-tight text-text-primary">Users</h1>
          <p className="mt-1 max-w-2xl text-sm text-text-muted">Manage identities, verification state, access, and sessions for this project.</p>
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="User summary">
        {[
          { label: "Total users", value: summary.total, Icon: Users, tone: "text-primary" },
          { label: "Verified", value: summary.verified, Icon: CheckCircle2, tone: "text-success" },
          { label: "Pending email", value: summary.pending, Icon: Clock3, tone: "text-warning" },
          { label: "Disabled", value: summary.disabled, Icon: Ban, tone: "text-text-muted" },
        ].map(({ label, value, Icon, tone }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-text-muted">{label}</p>
              <Icon className={`h-4 w-4 ${tone}`} />
            </div>
            <p className="mt-2 text-2xl font-semibold text-text-primary">{value}</p>
          </div>
        ))}
      </section>

      <section className="card p-3 sm:p-4">
        <div className="grid gap-3 md:grid-cols-[minmax(14rem,1fr)_11rem_13rem]">
          <label className="relative">
            <span className="sr-only">Search users</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input className="input w-full !pl-9 text-sm" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search email or username…" />
          </label>
          <select aria-label="Filter status" className="input text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option><option value="active">Active</option><option value="anonymous">Anonymous</option><option value="disabled">Disabled</option>
          </select>
          <select aria-label="Sort users" className="input text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="-created_at">Newest first</option><option value="created_at">Oldest first</option><option value="-last_sign_in_at">Recently signed in</option><option value="email">Email A–Z</option>
          </select>
        </div>
      </section>

      {error ? <ErrorBox error={error} /> : null}
      {impersonation ? (
        <section className="card border border-warning/40 p-4" data-testid="impersonation-result">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="font-medium text-text-primary">Impersonation token issued</p>
              <p className="mt-1 text-xs text-text-muted">Expires {fmt(impersonation.expires_at)}. Copy once; never commit or log this token.</p>
              <code className="mt-3 block max-h-24 overflow-auto break-all rounded-md bg-surface-container-low p-2 text-[11px]">{impersonation.access_token}</code>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="outline" onClick={() => void navigator.clipboard.writeText(impersonation.access_token)}>Copy</Button>
              <Button size="sm" variant="outline" onClick={() => setImpersonation(null)}>Dismiss</Button>
            </div>
          </div>
        </section>
      ) : null}
      {loading ? <Spinner label="Loading users…" /> : users.length === 0 ? (
        <EmptyState title="No users found">Try another filter, or create the first user through your app’s signup flow.</EmptyState>
      ) : (
        <>
          <div className="grid gap-3 md:hidden">
            {users.map((user) => (
              <article key={user.id} className="card p-4">
                <div className="flex items-start justify-between gap-3"><UserIdentity user={user} />{actions(user)}</div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border-standard pt-3 text-xs">
                  <div><p className="text-text-muted">Status</p><p className="mt-1 capitalize text-text-primary">{user.status}</p></div>
                  <div><p className="text-text-muted">Email</p><p className="mt-1 text-text-primary">{user.email ? (user.email_verified ? "Verified" : "Pending") : "Not set"}</p></div>
                  <div><p className="text-text-muted">Created</p><p className="mt-1 text-text-primary">{fmt(user.created_at)}</p></div>
                  <div><p className="text-text-muted">Last sign-in</p><p className="mt-1 text-text-primary">{fmt(user.last_sign_in_at)}</p></div>
                </div>
              </article>
            ))}
          </div>
          <div className="table-wrap hidden md:block">
            <table className="data">
              <thead><tr><th>User</th><th>Verification</th><th>Status</th><th>Created</th><th>Last sign-in</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>{users.map((user) => (
                <tr key={user.id}>
                  <td><UserIdentity user={user} /></td>
                  <td>{user.email ? <span className={user.email_verified ? "text-success" : "text-warning"}>{user.email_verified ? "Verified" : "Pending"}</span> : <span className="text-text-muted">No email</span>}</td>
                  <td><span className="capitalize">{user.status}</span></td>
                  <td className="text-text-muted">{fmt(user.created_at)}</td>
                  <td className="text-text-muted">{fmt(user.last_sign_in_at)}</td>
                  <td>{busyID === user.id ? <Spinner label="Updating…" /> : actions(user)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      <Dialog open={Boolean(verifyTarget)} onOpenChange={(open) => !open && setVerifyTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <div className="mb-2 grid h-10 w-10 place-items-center rounded-full bg-success/10 text-success"><ShieldCheck className="h-5 w-5" /></div>
            <DialogTitle>Verify this email manually?</DialogTitle>
            <DialogDescription>
              This immediately marks <strong className="text-text-primary">{verifyTarget?.email}</strong> as verified and invalidates outstanding verification codes. Only do this after confirming ownership outside TinyBase.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyTarget(null)}>Cancel</Button>
            <Button onClick={() => void verifyEmail()} disabled={Boolean(busyID)}>
              <UserRound className="mr-2 h-4 w-4" /> Confirm verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
